import { useState, useEffect } from 'react'
import { TrendingUp, ArrowUpDown, RefreshCw } from 'lucide-react'

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function ExchangeRates() {
  const [exchangeRate, setExchangeRate] = useState({
    buy: "19.80",
    sell: "20.20",
    loading: true,
    lastUpdate: ""
  })

  const [currencies, setCurrencies] = useState([
    { name: "Dólar", code: "USD", flag: "🇺🇸", buy: "18.0100", sell: "19.0500" },
    { name: "Euro", code: "EUR", flag: "🇪🇺", buy: "21.4225", sell: "21.4408" },
    { name: "Libra", code: "GBP", flag: "🇬🇧", buy: "24.3927", sell: "24.4323" },
    { name: "Yen", code: "JPY", flag: "🇯🇵", buy: "0.12044", sell: "0.12063" }
  ])

  const [loadingCurrencies, setLoadingCurrencies] = useState(true)

  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch(`${API_URL}/api/rates`)
        if (!response.ok) throw new Error('Error al obtener datos')
        const rates = await response.json()
        setExchangeRate({
          buy: rates.buy,
          sell: rates.sell,
          loading: false,
          lastUpdate: rates.lastUpdate
        })
      } catch (error) {
        console.error('Error al cargar tipo de cambio:', error)
        setExchangeRate({
          buy: "19.80",
          sell: "20.20",
          loading: false,
          lastUpdate: "Error al cargar"
        })
      }
    }
    fetchExchangeRate()
    const interval = setInterval(fetchExchangeRate, 30000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/MXN')
        if (!response.ok) throw new Error('Error al obtener divisas')
        const data = await response.json()

        const usdRate = 1 / data.rates.USD
        const eurRate = 1 / data.rates.EUR
        const gbpRate = 1 / data.rates.GBP
        const jpyRate = 1 / data.rates.JPY

        const formatCurrency = (rate, isSell = false) => {
          const margin = isSell ? 1.015 : 0.985
          return (rate * margin).toFixed(4)
        }

        setCurrencies([
          { name: "Dólar", code: "USD", flag: "🇺🇸", buy: formatCurrency(usdRate), sell: formatCurrency(usdRate, true) },
          { name: "Euro", code: "EUR", flag: "🇪🇺", buy: formatCurrency(eurRate), sell: formatCurrency(eurRate, true) },
          { name: "Libra", code: "GBP", flag: "🇬🇧", buy: formatCurrency(gbpRate), sell: formatCurrency(gbpRate, true) },
          { name: "Yen", code: "JPY", flag: "🇯🇵", buy: formatCurrency(jpyRate), sell: formatCurrency(jpyRate, true) }
        ])
        setLoadingCurrencies(false)
      } catch (error) {
        console.error('Error al obtener divisas:', error)
        setLoadingCurrencies(false)
      }
    }
    fetchCurrencies()
    const interval = setInterval(fetchCurrencies, 300000)
    return () => clearInterval(interval)
  }, [])

  return (
    <section className="exchange-rates-section" id="stats-section">
      <div className="max-w-6xl mx-auto px-4">
        
        {/* Tarjeta principal: Tipo de Cambio Hoy */}
        <div className="exchange-main-card">
          <div className="exchange-main-inner">
            <div className="exchange-main-header">
              <div className="exchange-main-icon">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div>
                <h2 className="exchange-main-title">Tipo de Cambio Hoy</h2>
                <p className="exchange-main-subtitle">Dólar USD / Peso MXN</p>
              </div>
            </div>

            {exchangeRate.loading ? (
              <div className="flex justify-center py-8">
                <div 
                  className="inline-block animate-spin rounded-full h-8 w-8 border-b-2" 
                  style={{ borderColor: 'var(--brand-red)' }}
                ></div>
              </div>
            ) : (
              <div className="exchange-main-rates">
                <div className="exchange-rate-box exchange-rate-buy">
                  <span className="exchange-rate-label">Compra</span>
                  <span className="exchange-rate-value">${exchangeRate.buy}</span>
                  <span className="exchange-rate-currency">MXN</span>
                </div>
                <div className="exchange-rate-divider">
                  <ArrowUpDown className="w-5 h-5" style={{ color: 'var(--muted-2)' }} />
                </div>
                <div className="exchange-rate-box exchange-rate-sell">
                  <span className="exchange-rate-label">Venta</span>
                  <span className="exchange-rate-value">${exchangeRate.sell}</span>
                  <span className="exchange-rate-currency">MXN</span>
                </div>
              </div>
            )}

            {exchangeRate.lastUpdate && (
              <p className="exchange-main-updated">
                <RefreshCw className="w-3 h-3" />
                Actualizado: {exchangeRate.lastUpdate}
              </p>
            )}
          </div>
        </div>

        {/* Grid de otras divisas */}
        <div className="exchange-currencies-grid">
          {loadingCurrencies ? (
            <div className="col-span-full flex justify-center py-8">
              <div 
                className="inline-block animate-spin rounded-full h-6 w-6 border-b-2" 
                style={{ borderColor: 'var(--brand-red)' }}
              ></div>
            </div>
          ) : (
            currencies.map((currency, index) => (
              <div key={index} className="exchange-currency-card">
                <div className="exchange-currency-header">
                  <span className="exchange-currency-flag">{currency.flag}</span>
                  <div>
                    <span className="exchange-currency-name">{currency.name}</span>
                    <span className="exchange-currency-code">{currency.code}</span>
                  </div>
                </div>
                <div className="exchange-currency-rates">
                  <div className="exchange-currency-rate">
                    <span className="exchange-currency-rate-label">Compra</span>
                    <span className="exchange-currency-rate-value">${currency.buy}</span>
                  </div>
                  <div className="exchange-currency-rate">
                    <span className="exchange-currency-rate-label">Venta</span>
                    <span className="exchange-currency-rate-value">${currency.sell}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <p className="exchange-disclaimer">
          * Los tipos de cambio de divisas internacionales se actualizan cada 5 minutos
        </p>
      </div>
    </section>
  )
}
